/**
 * 专享算力页面：按数量开机与手动关机。
 */

import type { ReactElement } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity,
  CreditCard,
  LayoutDashboard,
  Loader2,
  Plus,
  Power,
  RotateCcw,
  Server,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores'
import {
  useExclusiveSettings,
  useExclusiveServers,
  useStartExclusiveServers,
  useStopExclusiveServer,
} from '@/hooks/useExclusiveCompute'
import type {
  HealthStatus,
  PowerState,
} from '@/types/exclusiveCompute'

/**
 * 获取电源状态文案
 */
function getPowerStateLabel(state: PowerState): string {
  switch (state) {
    case 'running':
      return '运行中'
    case 'starting':
      return '开机中'
    case 'stopping':
      return '关机中'
    case 'stopped':
      return '已关机'
    default:
      return '未知'
  }
}

/**
 * 获取电源状态颜色
 */
function getPowerStateColor(state: PowerState): string {
  switch (state) {
    case 'running':
      return 'text-emerald-500'
    case 'starting':
      return 'text-blue-500'
    case 'stopping':
      return 'text-amber-500'
    case 'stopped':
      return 'text-muted-foreground'
    default:
      return 'text-muted-foreground'
  }
}

/**
 * 获取健康状态指示灯
 */
function HealthIndicator({ status }: { status: HealthStatus }): ReactElement {
  const colorClass =
    status === 'healthy' ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'
  return (
    <div className="flex items-center gap-1.5">
      <span className={cn('h-2 w-2 rounded-full', colorClass)} />
      <span className="text-xs text-muted-foreground">
        {status === 'healthy' ? '健康' : '异常'}
      </span>
    </div>
  )
}

/**
 * 专享算力页面组件
 */
export function ExclusiveComputePage(): ReactElement {
  const user = useAuthStore((state) => state.user)
  const computeType = user?.company?.computeType ?? 'shared'

  const canQueryExclusive = user !== null && computeType === 'exclusive'
  const settingsQuery = useExclusiveSettings(canQueryExclusive)
  const serversQuery = useExclusiveServers(canQueryExclusive)
  const startMutation = useStartExclusiveServers()
  const stopMutation = useStopExclusiveServer()

  const [startCountInput, setStartCountInput] = useState<string>('1')
  const [isStoppingAll, setIsStoppingAll] = useState(false)

  const settings = settingsQuery.data
  const activeServers = useMemo(
    () => (serversQuery.data?.list ?? []).filter((server) => server.power_state !== 'stopped'),
    [serversQuery.data?.list]
  )

  // Stats
  const startingCount = activeServers.filter((s) => s.power_state === 'starting').length
  const runningServers = activeServers.filter((s) => s.server_kind === 'cloud' && s.power_state === 'running')
  const runningCount = runningServers.length
  const stoppingCount = activeServers.filter((s) => s.power_state === 'stopping').length
  const totalInFlight = activeServers.reduce((acc, s) => acc + s.in_flight, 0)
  const maxCapacity = activeServers.reduce((acc, s) => acc + s.max_concurrent, 0)
  const capacityUtilization = maxCapacity > 0 ? Math.round((totalInFlight / maxCapacity) * 100) : 0

  const parsedStartCount = Number(startCountInput)
  const maxRunning = settings?.maxRunning ?? 0
  const isStartCountValid =
    Number.isFinite(parsedStartCount) &&
    parsedStartCount >= 1 &&
    parsedStartCount <= maxRunning

  /**
   * 批量开机
   */
  const handleStartServers = useCallback((): void => {
    if (!isStartCountValid) {
      toast.error('开机数量不合法')
      return
    }
    startMutation.mutate({
      count: Math.floor(parsedStartCount),
      mode: 'incremental',
    }, {
      onSuccess: (data) => {
        if (data.queuedCount === 0) {
          toast.info('当前已有足够实例')
        } else {
          toast.success(`已提交 ${String(data.queuedCount)} 台开机请求`)
        }
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : '请求失败')
      },
    })
  }, [isStartCountValid, parsedStartCount, startMutation])

  /**
   * 全部关机
   */
  const handleStopAll = useCallback(async (): Promise<void> => {
    if (runningServers.length === 0) return
    setIsStoppingAll(true)
    try {
      await Promise.all(runningServers.map(s => stopMutation.mutateAsync(s.id)))
      toast.success('已发送全量关机指令')
    } catch {
      toast.warning('部分关机请求可能发送失败，请稍后刷新查看')
    } finally {
      setIsStoppingAll(false)
    }
  }, [runningServers, stopMutation])

  if (user === null || computeType !== 'exclusive') {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-4 text-center">
        <div className="rounded-full bg-secondary p-4">
          <Server className="h-8 w-8 text-muted-foreground" />
        </div>
        <h1 className="text-2xl font-bold">无访问权限</h1>
        <p className="max-w-md text-muted-foreground">
          此页面仅对开通了【专享算力】的企业用户开放。如有需求，请联系客服或升级套餐。
        </p>
        <Button asChild>
          <Link to={user ? "/pricing" : "/login"}>{user ? "查看套餐" : "去登录"}</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-20">
      {/* 顶部标题与导航 */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <LayoutDashboard className="h-6 w-6 text-primary" />
            算力管理控制台
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            实时监控实例状态，灵活调度算力资源。
          </p>
        </div>
        <div className="flex items-center gap-2">
           <Button variant="outline" size="sm" onClick={(): void => { void serversQuery.refetch() }}>
            <RotateCcw className={cn("mr-2 h-3.5 w-3.5", serversQuery.isFetching && "animate-spin")} />
            刷新状态
          </Button>
          <Button asChild variant="secondary" size="sm">
            <Link to="/workflows">去生图</Link>
          </Button>
        </div>
      </div>

      {/* 核心指标概览 */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">运行实例</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{runningCount}</div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
              {startingCount > 0 && <span className="text-blue-500">+{startingCount} 启动中</span>}
              {stoppingCount > 0 && <span className="text-amber-500">-{stoppingCount} 关机中</span>}
              {startingCount === 0 && stoppingCount === 0 && <span>运行平稳</span>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">任务负载</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{capacityUtilization}%</div>
            <Progress value={capacityUtilization} className="h-1.5 mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              当前处理任务 {totalInFlight} / 并发容量 {maxCapacity}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">算力余额</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{user.company?.balance ?? 0}</div>
            <div className="mt-1 flex items-center justify-between">
               <span className="text-xs text-muted-foreground">每10分钟/台 消耗 100 算力</span>
               <Link to="/pricing" className="text-xs font-medium text-primary hover:underline">
                 充值 &rarr;
               </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 快捷控制面板 */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
           <div className="space-y-1">
             <h3 className="font-semibold text-foreground flex items-center gap-2">
               <Zap className="h-4 w-4 text-primary" />
               资源调度
             </h3>
             <p className="text-sm text-muted-foreground">
               调整云端实例数量，满足临时算力需求。最大可开 {maxRunning} 台。
             </p>
           </div>

           <div className="flex items-center gap-3">
             <div className="flex items-center rounded-md border bg-background shadow-sm">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-r-none"
                  onClick={() => { setStartCountInput(p => String(Math.max(1, Number(p) - 1))); }}
                >
                  <span className="text-lg">-</span>
                </Button>
                <div className="w-16 text-center border-x py-1.5 text-sm font-medium">
                  <input
                    className="w-full text-center bg-transparent outline-none"
                    value={startCountInput}
                    onChange={(e) => { setStartCountInput(e.target.value); }}
                    type="number"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-l-none"
                  onClick={() => { setStartCountInput(p => String(Math.min(maxRunning, Number(p) + 1))); }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
             </div>

             <Button
               onClick={handleStartServers}
               disabled={!isStartCountValid || startMutation.isPending}
             >
               {startMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
               增加实例
             </Button>

             <AlertDialog>
               <AlertDialogTrigger asChild>
                 <Button variant="destructive" disabled={runningCount === 0 || isStoppingAll}>
                    <Power className="mr-2 h-4 w-4" />
                    一键关机
                 </Button>
               </AlertDialogTrigger>
               <AlertDialogContent>
                 <AlertDialogHeader>
                   <AlertDialogTitle>确认关闭所有运行中实例？</AlertDialogTitle>
                   <AlertDialogDescription>
                     将对 {runningCount} 台运行中的云实例发送关机指令。实例关闭后将停止计费，但正在进行的任务可能会被中断。
                   </AlertDialogDescription>
                 </AlertDialogHeader>
                 <AlertDialogFooter>
                   <AlertDialogCancel>取消</AlertDialogCancel>
                   <AlertDialogAction onClick={(): void => { void handleStopAll() }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                     确认关机
                   </AlertDialogAction>
                 </AlertDialogFooter>
               </AlertDialogContent>
             </AlertDialog>
           </div>
        </CardContent>
      </Card>

      {/* 实例列表 */}
      <div className="space-y-4">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-lg font-semibold">实例列表 ({activeServers.length})</h2>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
             <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500"></span> 运行中</span>
             <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500"></span> 启动中</span>
             <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500"></span> 关机中</span>
          </div>
        </div>

        {activeServers.length === 0 ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center rounded-xl border border-dashed bg-muted/30 text-center">
            <Server className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm font-medium text-foreground">暂无活跃实例</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">
              所有实例均已关机或释放。请使用上方的控制面板启动新实例。
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {activeServers.map((server) => (
              <Card key={server.id} className="overflow-hidden transition-all hover:shadow-md">
                <div className={cn("h-1 w-full",
                  server.power_state === 'running' ? 'bg-emerald-500' :
                  server.power_state === 'starting' ? 'bg-blue-500 animate-pulse' :
                  'bg-amber-500'
                )} />
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{server.name || '未命名实例'}</span>
                        {server.server_kind === 'onprem' && (
                           <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px]">自建</span>
                        )}
                      </div>
                      <div className={cn("text-xs font-medium flex items-center gap-1.5", getPowerStateColor(server.power_state))}>
                         {server.power_state === 'starting' && <Loader2 className="h-3 w-3 animate-spin" />}
                         {getPowerStateLabel(server.power_state)}
                      </div>
                    </div>
                    <HealthIndicator status={server.health_status} />
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-2">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">并发任务</span>
                      <span>{server.in_flight} / {server.max_concurrent}</span>
                    </div>
                    <Progress value={(server.in_flight / Math.max(1, server.max_concurrent)) * 100} className="h-1.5" />

                    <div className="flex items-center justify-between pt-2 border-t mt-3">
                       <span className="text-[10px] text-muted-foreground font-mono truncate max-w-[120px]" title={server.id}>ID: {server.id.slice(0,8)}...</span>
                       {server.server_kind === 'cloud' && server.power_state === 'running' && (
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-6 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                           onClick={() => { stopMutation.mutate(server.id, {
                             onSuccess: () => toast.success(`实例 ${server.name || server.id.slice(0, 8)} 已关机成功`),
                             onError: (error) => toast.error(error instanceof Error ? error.message : '关机请求失败'),
                           }); }}
                           disabled={stopMutation.isPending}
                         >
                           关机
                         </Button>
                       )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
